import { useState } from "react";
import { useSearch, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { CheckCircle } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useActiveOrganization } from "@/contexts/ActiveOrganizationContext";
import { dataApi, type Company } from "@/lib/api/admin-api";
import { useLingui } from "@lingui/react/macro";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { ListSectionHeader } from "@/components/common/ListSectionHeader";
import { ApiDatatable } from "@/components/data-table";
import { getCompanyColumns } from "./columns";
import { CompanyTile } from "./CompanyTile";
import { CompanyFormDialog, CreateCompanyDialog } from "./CreateCompanyDialog";

export function CompanyListing() {
  const { t, i18n } = useLingui();
  const { isAuthenticated } = useAuth();
  const { activeOrganization } = useActiveOrganization();
  const searchParams = useSearch({
    from: "/(protected)/_layout/_org/companies/",
  });
  const navigate = useNavigate();

  const [editingCompany, setEditingCompany] = useState<Company | null>(null);
  const [total, setTotal] = useState<number>();

  const { data: organizations = [] } = useQuery({
    queryKey: ["organizations", "for-companies-table"],
    queryFn: () => dataApi.getOrganizations({ adminScope: true }),
    enabled: isAuthenticated,
  });

  const getOrganizationName = (company: Company) => {
    if (company.organization?.name) return company.organization.name;
    const organization = organizations.find(
      (org) => org.id === company.organizationId,
    );
    return organization?.name ?? "—";
  };

  const navigateToCompany = (id: number | string) =>
    navigate({
      to: "/companies/$companyId",
      params: { companyId: String(id) },
    });

  const columns = getCompanyColumns(
    i18n,
    navigateToCompany,
    setEditingCompany,
    getOrganizationName,
  );

  return (
    <div className="flex flex-col gap-3">
      <ListSectionHeader title={t`Concepts`} count={total}>
        <Button
          variant="outline"
          className="h-10 gap-2 rounded-[10px] border-(--shell-border) text-sm font-medium text-(--color-text-primary)"
        >
          <CheckCircle className="size-4" />
          {t`Select`}
        </Button>
        <CreateCompanyDialog />
      </ListSectionHeader>
      <ApiDatatable
        searchParams={searchParams}
        onNavigate={(updater) =>
          void navigate({ from: "/companies/", search: updater, replace: true })
        }
        onTotalChange={setTotal}
        queryKey={["companies", activeOrganization?.id ?? null]}
        queryFn={(params) =>
          dataApi.paginateCompanies({
            search: params.search,
            page: params.page,
            limit: params.pageSize,
            sortBy: params.sortBy,
            sortOrder: params.sortOrder,
            isActive:
              params.status === "active"
                ? true
                : params.status === "inactive"
                  ? false
                  : undefined,
            include: ["address"],
            organizationId: activeOrganization?.id,
          })
        }
        columns={columns}
        renderTile={(company) => (
          <CompanyTile
            company={company}
            onViewDetails={(c) => navigateToCompany(c.id)}
          />
        )}
        getRowId={(company) => String(company.id)}
        onRowClick={(company) => navigateToCompany(company.id)}
        enabled={isAuthenticated}
        searchPlaceholder={t`Search concepts…`}
        emptyMessage={t`No concepts found`}
        toolbarExtra={
          <Select
            value={searchParams.status}
            onValueChange={(status) =>
              void navigate({
                from: "/companies/",
                search: (prev) => ({
                  ...prev,
                  status: status as "all" | "active" | "inactive",
                  page: 1,
                }),
              })
            }
          >
            <SelectTrigger className="w-35">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t`All`}</SelectItem>
              <SelectItem value="active">{t`Active`}</SelectItem>
              <SelectItem value="inactive">{t`Inactive`}</SelectItem>
            </SelectContent>
          </Select>
        }
      />

      <CompanyFormDialog
        company={editingCompany ?? undefined}
        open={!!editingCompany}
        onOpenChange={(open) => {
          if (!open) setEditingCompany(null);
        }}
      />
    </div>
  );
}
